








































































































































































































































































import axios from 'axios';
import moment from 'moment-timezone';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import CreateInventoryView from '@/components/inventory/CreateInventoryView.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiTooltip from '@/components/ui/UiTooltip.vue';
import UiTruncateText from '@/components/ui/UiTruncateText.vue';
import WalletListNew from '@/components/wallets/WalletListNew.vue';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { downloadAuthorizedFile } from '@/utils/downloadFile';
import numberUtils from '@/utils/numberUtils';
import { downloadReportRunCsv, pollReportRunStatus, submitReportRun } from '@/utils/reportRunsV3';

import { baConfig } from '../../../../config';
import {
  ApiSvcGainLossSummary,
  ApiSvcInventoryCostBasisRollForwardLine,
  ApiSvcInventoryView,
  ApiSvcTaxStrategyType,
  InventoryApi,
} from '../../../../generated/api-svc';

// used to add isTotal property to the line
export interface DataTableLine extends ApiSvcInventoryCostBasisRollForwardLine {
  isTotal?: boolean;
  /** Present only on advanced-endpoint rows when grouping by inventory group. */
  inventory?: string;
}

@Component({
  components: {
    UiCheckbox,
    CreateInventoryView,
    UiDatePicker,
    UiButton,
    WalletListNew,
    UiDataTable,
    UiTooltip,
    UiTruncateText,
  },
})
export default class CostBasisRollForwardReport extends BaseVue {
  @Prop({ default: null })
  public readonly view!: ApiSvcInventoryView | null;

  public lines: DataTableLine[] = [];
  public summary: ApiSvcGainLossSummary[] = [];

  public startDate = moment.tz(moment.tz.guess()).subtract(1, 'day').format('YYYY-MM-DD');
  public endDate = moment.tz(moment.tz.guess()).subtract(1, 'day').format('YYYY-MM-DD');

  public numFormat = numberUtils.getFormatter({ accountingNegative: true });
  public unrealizedGainLoss = 0;
  public includeFmv = false;
  public excludeInternalTransfersFromCostBasisCalculations = false;
  public groupByInventory = false;

  /**
   * The inventory-group breakout only means something on views that map
   * wallets into inventories; gate the control on the expanded-report flag
   * while the legacy "(Expanded)" tile is still the other way to reach it.
   */
  public get showInventoryBreakout() {
    return (
      this.checkFeatureFlag('cost-basis-roll-forward-v2') &&
      (this.view?.inventoryConfig?.inventoryMappingRule?.type === 'inventory-group-mapping' ||
        this.view?.inventoryConfig?.inventoryMappingRule?.type === 'inventory-per-wallet')
    );
  }

  public get useInventoryBreakout() {
    return this.groupByInventory && this.showInventoryBreakout;
  }

  public reportElapsedTime? = '';

  public getGainLossClass(gl: number) {
    if (gl > 0) {
      return 'tw-text-green-500';
    } else if (gl < 0) {
      return 'tw-text-red-500';
    } else {
      return '';
    }
  }

  public get isAverageCost() {
    return this.view?.inventoryPickingStrategy.type === ApiSvcTaxStrategyType.NUMBER_4;
  }

  public get headers() {
    const headers = [
      ...(this.useInventoryBreakout
        ? [
            {
              id: 'inventory',
              label: 'Inventory Group',
              defaultVisibility: true,
            },
          ]
        : []),
      {
        id: 'asset',
        label: 'Asset',
        defaultVisibility: true,
      },
      {
        id: 'starting_qty_value',
        label: 'Qty (beg)',
        defaultVisibility: true,
      },
      {
        id: 'starting_fiat_value',
        label: this.view?.impair ? 'Carrying Value (beg)' : 'Cost Basis (beg)',
        defaultVisibility: true,
      },
      {
        id: 'qty_increases',
        label: 'Qty (acq)',
        defaultVisibility: true,
      },
      {
        id: 'fiat_increases',
        label: 'Cost Basis (acq)',
        defaultVisibility: true,
      },
      ...(this.excludeInternalTransfersFromCostBasisCalculations
        ? [
            {
              id: 'internal_cost_basis_acquired',
              label: 'Internal Cost Basis Acquired',
              defaultVisibility: true,
            },
          ]
        : []),
      {
        id: 'qty_decreases',
        label: 'Qty (disp)',
        defaultVisibility: true,
      },
      {
        id: 'fiat_decreases',
        label: 'Proceeds From Disposal',
        defaultVisibility: true,
        groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
      },
      ...(this.excludeInternalTransfersFromCostBasisCalculations
        ? [
            {
              id: 'internal_cost_basis_disposed',
              label: 'Internal Cost Basis Disposed',
              defaultVisibility: true,
              groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
            },
          ]
        : []),
      {
        id: 'st_gain_loss',
        label: 'ST Gain Loss',
        defaultVisibility: true,
        groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
      },
    ];
    if (this.isAverageCost) {
      headers.push({
        id: 'un_gain_loss',
        label: 'Undated Gain Loss',
        defaultVisibility: true,
        groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
      });
    }

    headers.push({
      id: 'lt_gain_loss',
      label: 'LT Gain Loss',
      defaultVisibility: true,
      groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
    });

    // if (this.view?.impair) {
    //   headers.push({
    //     id: 'un_gain_loss',
    //     label: 'Undated Gain Loss',
    //     defaultVisibility: true,
    //     groupLabel: this.view?.impair ? 'Carrying Value Disposed' : 'Cost Basis Disposed',
    //   });
    // }

    if (this.view?.impair) {
      headers.push({
        id: 'impairExpense_fiat_decreases',
        label: 'Impairment Expense',
        defaultVisibility: true,
      });
      headers.push({
        id: 'impairExpenseReversal_fiat_increases',
        label: 'Impairment Reversal',
        defaultVisibility: true,
      });
    }

    headers.push();

    headers.push(
      {
        id: 'ending_qty_value',
        label: 'Qty (end)',
        defaultVisibility: true,
      },
      {
        id: 'ending_fiat_value',
        label: this.view?.impair ? 'Carrying Value (end)' : 'Cost Basis (end)',
        defaultVisibility: true,
      }
    );

    if (this.includeFmv) {
      headers.push(
        {
          id: 'current_price',
          label: 'Current Price',
          defaultVisibility: true,
        },
        {
          id: 'fair_market_value',
          label: 'Fair Market Value',
          defaultVisibility: true,
        },
        {
          id: 'unrealized_gain_loss',
          label: 'Unrealized Gain/Loss',
          defaultVisibility: true,
        }
      );
    }

    return headers;
  }

  public isLoading = false;

  async runReport() {
    if (this.view && this.view.id) {
      this.isLoading = true;
      this.reportElapsedTime = undefined;
      const reportStartTime = Date.now();
      this.unrealizedGainLoss = 0;
      try {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        // The per-asset endpoint has no notion of inventory groups; grouped
        // runs go through the advanced endpoint at the inventory/asset grain.
        const p1 = this.useInventoryBreakout
          ? svc.getAdvancedCostBasisRollForward(
              this.orgId,
              this.view.id,
              this.startDate,
              this.endDate,
              true,
              false,
              false,
              false,
              this.includeFmv,
              false,
              this.excludeInternalTransfersFromCostBasisCalculations,
              {
                withCredentials: true,
              }
            )
          : svc.getInventoryCostBasisRollForward(
              this.orgId,
              this.view.id,
              this.startDate,
              this.endDate,
              false,
              this.includeFmv,
              false,
              this.excludeInternalTransfersFromCostBasisCalculations,
              {
                withCredentials: true,
              }
            );

        const p2 = svc.getsGainLossSummary(this.orgId, this.view.id, this.startDate, this.endDate, undefined, {
          withCredentials: true,
        });

        const [resp, summaryResp] = await Promise.all([p1, p2]);

        if (resp.status === 200 && summaryResp.status === 200) {
          this.lines = resp.data.lines;

          const _myNum = (x: any) => {
            if (typeof x === 'string') {
              return isNaN(Number(x)) ? 0 : Number(x);
            } else if (typeof x === 'undefined') {
              return 0;
            } else {
              return isNaN(x) ? 0 : x;
            }
          };

          const _totalKeys = this.headers.map((x) => x.id);
          const _totals = this.lines.reduce((acc: any, next: any) => {
            if (this.useInventoryBreakout) {
              acc.inventory = 'TOTAL';
              acc.asset = '-';
            } else {
              acc.asset = 'TOTAL';
            }

            for (const key of _totalKeys) {
              if (key === 'asset' || key === 'inventory') {
                continue;
              }

              // Summing a price is meaningless, and grouped rows repeat an
              // asset once per inventory group; match the Expanded page and
              // leave the total cell blank.
              if (key === 'current_price' && this.useInventoryBreakout) {
                acc.current_price = '-';
                continue;
              }

              let _key = key;

              // key mapping for correcting fields in headers
              if (key === 'st_gain_loss') {
                _key = next[key] ? 'st_gain_loss' : 'stGainLoss_fiat_decreases';
              }

              if (key === 'un_gain_loss') {
                _key = next[key] ? 'un_gain_loss' : 'unGainLoss_fiat_decreases';
              }

              if (key === 'lt_gain_loss') {
                _key = next[key] ? 'lt_gain_loss' : 'ltGainLoss_fiat_decreases';
              }

              if (key === 'fiat_decreases') {
                _key = next[key] ? 'fiat_decreases' : 'fmv_disposed_fiat_decreases';
              }

              if (key === 'unrealized_gain_loss') {
                if (!next.isTotal) {
                  this.unrealizedGainLoss += _myNum(next[key]);
                }
              }

              // first check if existing value is a string, if so convert to number
              acc[_key] = _myNum(acc[_key]);

              // add accumulated value to next value
              acc[_key] += _myNum(next[_key]);
            }

            return acc as DataTableLine;
          }, {} as DataTableLine);

          // add totals to the end of the lines
          this.lines.push({ ..._totals, isTotal: true });

          this.summary = summaryResp.data.summary;
        }
      } finally {
        this.isLoading = false;
        this.reportElapsedTime = this.getElapsedTime(reportStartTime);
      }
    }
  }

  public csvLoading = false;

  public baseUrl = getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL ?? process.env.VUE_APP_API_URL!);

  downloadFile = downloadAuthorizedFile;

  // Legacy `exportResults=true` builds the CSV straight from the report SQL,
  // so the FMV columns the screen shows (computed app-side after the query)
  // never reach the file. The V3 report builds the CSV from the same enriched
  // rows the UI renders; route the download there for flagged orgs.
  public get useV3CsvExport() {
    return this.checkFeatureFlag('cost-basis-roll-forward-v3');
  }

  private pollAborted = false;

  beforeDestroy() {
    this.pollAborted = true;
  }

  async downloadReport() {
    if (this.useV3CsvExport) {
      await this.downloadReportV3();
    } else {
      await this.downloadReportLegacy();
    }
  }

  async downloadReportV3() {
    if (!(this.view && this.view.id)) {
      return;
    }
    // Pin the org for the whole submit→poll→download flow: the poll can run
    // for minutes, and a mid-flight org switch must not send the later calls
    // to a different org than the one the run belongs to.
    const orgId = this.orgId;
    this.csvLoading = true;
    try {
      const runId = await submitReportRun(this.baseUrl, orgId, 'cost-basis-rollforward-report', [
        { key: 'inventoryViewId', value: this.view.id },
        { key: 'startDate', value: this.startDate },
        { key: 'endDate', value: this.endDate },
        { key: 'includeFmv', value: String(this.includeFmv) },
        {
          key: 'excludeInternalTransfersFromCostBasisCalculations',
          value: String(this.excludeInternalTransfersFromCostBasisCalculations),
        },
        ...(this.useInventoryBreakout ? [{ key: 'useInventory', value: 'true' }] : []),
      ]);

      const status = await pollReportRunStatus(this.baseUrl, orgId, runId, {
        intervalMs: 3000,
        timeoutMs: 10 * 60 * 1000,
        isAborted: () => this.pollAborted,
      });
      if (status === 'aborted') {
        // The user navigated away; the store-backed snackbar would outlive
        // this component, so don't surface anything for a deliberate exit.
        return;
      }
      if (status !== 'succeeded') {
        this.showErrorSnackbar(
          status === 'timed-out' ? 'Report timed out. Please try again.' : 'Report failed. Please try again.'
        );
        return;
      }

      await downloadReportRunCsv(
        this.baseUrl,
        orgId,
        runId,
        `cost-basis-rollforward-report-${this.startDate}-${this.endDate}.csv`
      );
    } catch (e) {
      this.showErrorSnackbar('Failed to download report. Please try again later.');
    } finally {
      this.csvLoading = false;
    }
  }

  async downloadReportLegacy() {
    if (this.view && this.view.id) {
      this.csvLoading = true;

      try {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        const p1 = this.useInventoryBreakout
          ? svc.getAdvancedCostBasisRollForward(
              this.orgId,
              this.view.id,
              this.startDate,
              this.endDate,
              true,
              false,
              false,
              true,
              this.includeFmv,
              false,
              this.excludeInternalTransfersFromCostBasisCalculations,
              {
                withCredentials: true,
              }
            )
          : svc.getInventoryCostBasisRollForward(
              this.orgId,
              this.view.id,
              this.startDate,
              this.endDate,
              true,
              this.includeFmv,
              false,
              this.excludeInternalTransfersFromCostBasisCalculations,
              {
                withCredentials: true,
              }
            );

        const resp = await p1;

        if (resp.status === 200) {
          const exportIds = resp.data.exportIds;
          const downloadUrlPromises = [] as any[];
          exportIds?.forEach((x: any) => {
            downloadUrlPromises.push(
              axios.get(`${this.baseUrl}v2/orgs/${this.$store.state.currentOrg.id}/exports/${x}?rawUrl=true`, {
                withCredentials: true,
              })
            );
          });
          const downloadUrls = await Promise.all(downloadUrlPromises);
          downloadUrls.forEach((x: any) => {
            this.downloadFile(x.data);
          });
        }
      } finally {
        this.csvLoading = false;
      }
    }
  }
}
